import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { PolicyType } from './policy-acceptance-types';

const TITLE_KEY_BY_POLICY_TYPE: Record<
  PolicyType,
  'consent:privacyNotice.title' | 'consent:termsAndConditions.title'
> = {
  'privacy-notice': 'consent:privacyNotice.title',
  'terms-and-conditions': 'consent:termsAndConditions.title',
};

const BODY_KEY_BY_POLICY_TYPE: Record<
  PolicyType,
  'consent:privacyNotice.body' | 'consent:termsAndConditions.body'
> = {
  'privacy-notice': 'consent:privacyNotice.body',
  'terms-and-conditions': 'consent:termsAndConditions.body',
};

/**
 * The blocking full-page card presenting every OUTSTANDING (unaccepted) policy's full text with a
 * single "Accetto"/"Accept" action (Resolution 8) — mirrors `LoginPage`'s own `Card` layout.
 * `onAcceptAll` POSTs each outstanding policy sequentially; this component owns only the
 * loading/error UI around that call, never the sequencing itself (`AcceptanceGate`'s job).
 */
export function PolicyAcceptanceScreen({
  outstanding,
  onAcceptAll,
}: {
  outstanding: { policyType: PolicyType; currentVersion: string }[];
  onAcceptAll: () => Promise<void>;
}) {
  const { t } = useTranslation(['consent']);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = () => {
    setSubmitting(true);
    setError(null);
    onAcceptAll()
      .catch(() => {
        setError(t('consent:gate.error'));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-[1320px] items-center justify-center p-[clamp(16px,2.4vw,28px)]">
      <div className="w-full max-w-lg space-y-6">
        <Card className="w-full">
          <CardHeader>
            {/* Blocking full-page screen shown before the Shell mounts, so this is the page's
                top-level heading — a real <h1> (WCAG 2.2 SC 1.3.1); policy titles below are <h2>. */}
            <CardTitle asChild>
              <h1>{t('consent:gate.title')}</h1>
            </CardTitle>
            <CardDescription>{t('consent:gate.intro')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {outstanding.map((policy) => {
              const bodyParagraphs = t(BODY_KEY_BY_POLICY_TYPE[policy.policyType], {
                returnObjects: true,
              });
              return (
                <div key={policy.policyType} className="space-y-2">
                  <h2 className="font-medium">{t(TITLE_KEY_BY_POLICY_TYPE[policy.policyType])}</h2>
                  <div className="max-h-64 space-y-2 overflow-y-auto text-sm text-foreground/80">
                    {bodyParagraphs.map((paragraph, index) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                </div>
              );
            })}
            <Button className="w-full" onClick={handleAccept} disabled={submitting}>
              {t('consent:gate.accept')}
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
